import { useMemo, useState } from "react";
import { planArchive, ARCHIVE_CAP_BYTES } from "../../utils/takeoffArchive";

// Choosing what goes into a project archive: the takeoff file always, plus the drawings you tick. Drawings without a
// loaded file are listed but cannot be ticked; ticking past the size limit is refused, with the reason shown.

const mb = (b) => (b / 1048576 >= 10 ? Math.round(b / 1048576) : Math.round((b / 1048576) * 10) / 10) + " MB";
const btn = (primary, disabled) => ({ fontSize: 12, fontWeight: 700, padding: "6px 14px", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, border: `1px solid ${primary ? "#2563eb" : "var(--cs-border)"}`, background: primary ? "#2563eb" : "var(--surface)", color: primary ? "#fff" : "var(--text)" });

export default function TakeoffArchiveExportDialog({ sheets, onExport, onCancel }) {
  const all = useMemo(() => planArchive({ sheets }), [sheets]);        // default: everything that fits
  const [selected, setSelected] = useState(() => new Set(all.items.filter(i => i.included).map(i => i.sheetId)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const plan = useMemo(() => planArchive({ sheets, selected }), [sheets, selected]);
  const used = plan.includedBytes;

  function toggle(it) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(it.sheetId)) next.delete(it.sheetId);
      else if (used + it.bytes <= ARCHIVE_CAP_BYTES) next.add(it.sheetId);
      return next;
    });
  }
  async function run() {
    setBusy(true); setError("");
    try { await onExport([...selected]); }
    catch (e) { setError(e.message || "The export failed."); setBusy(false); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 950, background: "rgba(15,23,42,0.5)", display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: "6vh" }}>
      <div role="dialog" aria-label="Export project archive" style={{ width: "min(640px, 94vw)", maxHeight: "86vh", overflowY: "auto", background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 10, boxShadow: "0 16px 48px rgba(0,0,0,0.4)", padding: "14px 18px", fontSize: 13 }}>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 2 }}>Export project archive</div>
        <div style={{ color: "var(--text3)", fontSize: 12, marginBottom: 10 }}>One zip with this revision's takeoff file and the drawings you tick. Built on this computer — nothing is uploaded.</div>

        {plan.items.length === 0 ? (
          <div style={{ color: "var(--text3)", marginBottom: 8 }}>This revision has no drawings; the archive will hold the takeoff file only.</div>
        ) : (
          <div style={{ marginBottom: 8 }}>
            {plan.items.map(it => {
              const fits = selected.has(it.sheetId) || used + it.bytes <= ARCHIVE_CAP_BYTES;
              const disabled = !it.available || !fits;
              return (
                <label key={it.sheetId} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", borderTop: "1px solid var(--cs-border)", opacity: disabled ? 0.55 : 1, cursor: disabled ? "not-allowed" : "pointer" }}>
                  <input type="checkbox" checked={selected.has(it.sheetId)} disabled={disabled} onChange={() => toggle(it)} />
                  <span style={{ flex: 1 }}>{it.label}</span>
                  <span style={{ color: "var(--text3)", fontSize: 12 }}>{it.available ? mb(it.bytes) : it.reason}{it.available && !fits && !selected.has(it.sheetId) ? " — would exceed the size limit" : ""}</span>
                </label>
              );
            })}
          </div>
        )}
        <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 8 }}>
          {selected.size} of {plan.items.length} drawing{plan.items.length === 1 ? "" : "s"} · {mb(used)} of {mb(ARCHIVE_CAP_BYTES)} limit
          {selected.size < plan.items.filter(i => i.available).length && <div style={{ color: "#b45309" }}>Drawings left out cannot be shown when the archive is imported elsewhere; their takeoff is still in the file and can be mapped to a drawing there.</div>}
        </div>
        {error && <div role="alert" style={{ color: "#dc2626", fontWeight: 700, marginBottom: 8 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onCancel} disabled={busy} style={btn(false, busy)}>Cancel</button>
          <button onClick={run} disabled={busy} style={btn(true, busy)}>{busy ? "Building…" : "Export archive"}</button>
        </div>
      </div>
    </div>
  );
}
